
"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from '@/hooks/useAuth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthSchema, type AuthFormValues } from '@/lib/schemas';
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AppLogo } from '@/components/AppLogo';
import { Loader2, LogIn, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { BiometricAuthService } from '@/services/BiometricAuthService';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Carregando } from '@/components/Carregando';

export default function LoginPage() {
  const { login, isLoading } = useAuth();
  const { toast } = useToast();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [saveBiometrics, setSaveBiometrics] = useState(false);

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(AuthSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function checkBiometricAvailability() {
    try {
      const available = await BiometricAuthService.isAvailable();
      if (available) {
        setIsBiometricAvailable(true);
        const hasSetup = localStorage.getItem('biometric_setup');
        if (hasSetup === 'true') {
          setSaveBiometrics(true);
        }
      }
    } catch (e) {
      console.log("Biometria não disponível:", e);
    }
  }

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const performBiometricLogin = async () => {
    try {
      setIsLoggingIn(true);
      const credentials = await BiometricAuthService.getCredentials();

      if (credentials && credentials.email && credentials.password) {
        form.setValue('email', credentials.email);
        form.setValue('password', credentials.password);

        toast({
          title: "Biometria Reconhecida",
          description: "Entrando no sistema...",
        });

        await handleLogin({ email: credentials.email, password: credentials.password });
      } else {
        setIsLoggingIn(false);
      }
    } catch (e) {
      console.error("Erro biometria:", e);
      setIsLoggingIn(false);
      toast({
        title: "Autenticação Falhou",
        description: "Não foi possível verificar sua identidade biométrica.",
        variant: "destructive"
      });
    }
  };

  const handleLogin = async (values: AuthFormValues) => {
    setIsLoggingIn(true);
    const { error } = await login(values);

    if (error) {
      toast({
        title: 'Erro de Login',
        description: error.message || 'Ocorreu um erro ao tentar fazer login.',
        variant: 'destructive',
      });
      setIsLoggingIn(false);
    } else {
      if (saveBiometrics && isBiometricAvailable) {
        try {
          await BiometricAuthService.setCredentials({
            email: values.email,
            password: values.password,
          });
          toast({
            title: "Biometria Configurada",
            description: "Próximos acessos poderão ser feitos com digital.",
          });
        } catch (e) {
          console.error("Erro ao salvar credenciais biométricas", e);
        }
      } else if (!saveBiometrics && isBiometricAvailable) {
        try {
          await BiometricAuthService.deleteCredentials();
        } catch (e) { }
      }

      window.location.reload();
    }
  };

  if (isLoading) {
    return (
      <Carregando telaCheia />
    );
  }

  return (
    <Card className="w-full max-w-sm shadow-lg">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4">
          <AppLogo tamanho="grande" />
        </div>
        <CardTitle className="text-2xl font-headline">Acessar Sistema</CardTitle>
        <CardDescription>
          Use seu email e senha para entrar.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-left w-full block">Email</FormLabel>
                  <FormControl className="mt-1">
                    <Input type="email" placeholder="seu@email.com" {...field} disabled={isLoggingIn} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-left w-full block">Senha</FormLabel>
                  <div className="relative mt-1">
                    <FormControl>
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        {...field}
                        disabled={isLoggingIn}
                        className="pr-10"
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 h-full px-3 text-muted-foreground hover:bg-transparent hover:text-foreground"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isBiometricAvailable && (
              <div className="flex items-center space-x-2 py-2">
                <Checkbox
                  id="biometrics"
                  checked={saveBiometrics}
                  onCheckedChange={(checked) => setSaveBiometrics(!!checked)}
                />
                <Label htmlFor="biometrics" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Habilitar login com biometria
                </Label>
              </div>
            )}

          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" size="lg" disabled={isLoggingIn}>
              {isLoggingIn ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="mr-2 h-4 w-4" />
              )}
              {isLoggingIn ? 'Entrando...' : 'Entrar'}
            </Button>

            {isBiometricAvailable && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-primary/20 hover:bg-primary/5 hover:text-primary"
                onClick={performBiometricLogin}
                disabled={isLoggingIn}
              >
                <Fingerprint className="mr-2 h-5 w-5 text-primary" />
                Entrar com Digital / FaceID
              </Button>
            )}

            <div className="flex justify-between w-full text-sm">
              <Button variant="link" asChild className="px-1 font-normal">
                <Link href="/forgot-password">
                  Esqueceu a senha?
                </Link>
              </Button>
              <Button variant="link" asChild className="px-1 font-normal">
                <Link href="/signup">
                  Criar uma conta
                </Link>
              </Button>
            </div>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
